// Heuristic per-region skin metrics extracted from a captured frame using
// FaceMesh landmarks. These are coarse cosmetic proxies (0..1), not clinical
// measures — the evaluator (LLM or rules) interprets them against the criteria.

// Approximate FaceMesh landmark indices grouped per region (centroid sampling).
export const REGION_LANDMARKS = {
  forehead: [10, 151, 9, 107, 336],
  leftCheek: [50, 205, 123, 116],
  rightCheek: [280, 425, 352, 345],
  nose: [1, 4, 5, 195],
  chin: [152, 175, 199, 200],
  underEyes: [230, 117, 450, 346],
};

// Region positions as fractions WITHIN the detected face box (0..1). Mapped into
// the box at sample time, so they land on the real face regardless of how much
// of the frame the face fills. If no box is available they fall back to the
// whole frame (centered-selfie assumption).
export const REGION_BOXES = {
  forehead: { x: 0.50, y: 0.16 },
  leftCheek: { x: 0.28, y: 0.55 },
  rightCheek: { x: 0.72, y: 0.55 },
  nose: { x: 0.50, y: 0.50 },
  chin: { x: 0.50, y: 0.90 },
  underEyes: { x: 0.50, y: 0.40 },
};

export function keypointsValid(kp) {
  return Array.isArray(kp) && kp.length >= 468 && kp[1] && typeof kp[1].x === 'number';
}

export function regionCentroid(keypoints, region) {
  const idxs = REGION_LANDMARKS[region] || [];
  let x = 0;
  let y = 0;
  let n = 0;
  for (const i of idxs) {
    const p = keypoints && keypoints[i];
    if (p) {
      x += p.x;
      y += p.y;
      n += 1;
    }
  }
  if (!n) return null;
  return { x: x / n, y: y / n };
}

function saturation(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function samplePatch(data, W, H, cx, cy, size) {
  const half = Math.floor(size / 2);
  const px = [];
  for (let y = cy - half; y <= cy + half; y += 1) {
    if (y < 0 || y >= H) continue;
    for (let x = cx - half; x <= cx + half; x += 1) {
      if (x < 0 || x >= W) continue;
      const i = (y * W + x) * 4;
      px.push([data[i], data[i + 1], data[i + 2]]);
    }
  }
  return px;
}

function scoreRegion(px) {
  const empty = { redness: 0, dryness: 0, dullness: 0, evenness: 0, shine: 0, spots: 0 };
  const n = px.length;
  if (!n) return empty;
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let sumLuma = 0;
  let sumSat = 0;
  let shineCount = 0;
  const lumas = [];
  for (const [r, g, b] of px) {
    sumR += r;
    sumG += g;
    sumB += b;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    sumLuma += luma;
    lumas.push(luma);
    sumSat += saturation(r, g, b);
    if (luma > 235) shineCount += 1;
  }
  const meanR = sumR / n;
  const meanG = sumG / n;
  const meanB = sumB / n;
  const meanLuma = sumLuma / n;
  const meanSat = sumSat / n;
  let varL = 0;
  for (const l of lumas) varL += (l - meanLuma) * (l - meanLuma);
  const stdL = Math.sqrt(varL / n);
  let spotCount = 0;
  for (const l of lumas) if (l < meanLuma - 28) spotCount += 1;

  return {
    redness: clamp01((meanR - (meanG + meanB) / 2) / 60),
    dryness: clamp01(((0.45 - meanSat) / 0.45) * 0.6 + (stdL / 70) * 0.4),
    dullness: clamp01(((150 - meanLuma) / 150) * 0.6 + ((0.4 - meanSat) / 0.4) * 0.4),
    evenness: clamp01(stdL / 55), // higher texture variance -> less even
    shine: clamp01(shineCount / n / 0.12),
    spots: clamp01(spotCount / n / 0.15),
  };
}

// Returns { metrics: { [region]: {...scores} }, positions: { [region]: {x,y} } }.
// Region centroids come from (in order of preference): the 468-pt mesh, the
// detected face box, or the whole frame.
export function extractMetrics(canvas, keypoints, box) {
  const metrics = {};
  const positions = {};
  if (!canvas) return { metrics, positions };
  const W = canvas.width;
  const H = canvas.height;
  let data;
  try {
    data = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
  } catch (e) {
    return { metrics, positions };
  }
  const useKp = keypointsValid(keypoints);
  let bw = W;
  let bh = H;
  let bx = 0;
  let byy = 0;
  if (!useKp && box) {
    bw = box.width != null ? box.width : box.xMax - box.xMin;
    bh = box.height != null ? box.height : box.yMax - box.yMin;
    bx = box.xMin;
    byy = box.yMin;
  }
  const patch = Math.max(8, Math.round((useKp ? Math.min(W, H) * 0.05 : Math.min(bw, bh) * 0.12)));
  for (const region of Object.keys(REGION_BOXES)) {
    let cx;
    let cy;
    if (useKp) {
      const c = regionCentroid(keypoints, region);
      if (c) {
        cx = c.x;
        cy = c.y;
      }
    }
    if (cx == null) {
      const b = REGION_BOXES[region];
      cx = bx + b.x * bw;
      cy = byy + b.y * bh;
    }
    metrics[region] = scoreRegion(samplePatch(data, W, H, Math.round(cx), Math.round(cy), patch));
    positions[region] = { x: cx / W, y: cy / H };
  }
  return { metrics, positions };
}
